import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiAward, FiLock, FiCheck, FiUnlock } from 'react-icons/fi';
import { useGame } from '../../contexts/GameContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ACHIEVEMENTS_LIST, RARITY_CONFIG } from '../../constants/achievements';
import SafeIcon from '../common/SafeIcon';

const AchievementsModal = ({ onClose }) => {
  const { state } = useGame();
  const { t } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRarity, setSelectedRarity] = useState('all');
  const [unlockFilter, setUnlockFilter] = useState('all'); // 🆕 'all', 'unlocked', 'locked'

  const categories = [
    { id: 'all', name: t('achievements.categories.all'), icon: '📋' },
    { id: 'tap', name: t('achievements.categories.tap'), icon: '🔔' },
    { id: 'combo', name: t('achievements.categories.combo'), icon: '⚡' },
    { id: 'speed', name: t('achievements.categories.speed'), icon: '🚀' },
    { id: 'type', name: t('achievements.categories.type'), icon: '🎭' },
    { id: 'auto', name: t('achievements.categories.auto'), icon: '🤖' },
    { id: 'special', name: t('achievements.categories.special'), icon: '✨' }
  ];

  // 🔧 修正:過濾出有效成就 (移除舊成就)
  const validAchievementIds = ACHIEVEMENTS_LIST.map(a => a.id);
  const validUnlockedAchievements = state.unlockedAchievements.filter(id =>
    validAchievementIds.includes(id)
  );

  const filteredAchievements = useMemo(() => {
    let filtered = ACHIEVEMENTS_LIST;

    // 類別篩選
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((a) => a.type === selectedCategory);
    }

    // 稀有度篩選
    if (selectedRarity !== 'all') {
      filtered = filtered.filter((a) => a.rarity === selectedRarity);
    }

    // 🆕 解鎖狀態篩選
    if (unlockFilter === 'unlocked') {
      filtered = filtered.filter((a) => validUnlockedAchievements.includes(a.id));
    } else if (unlockFilter === 'locked') {
      filtered = filtered.filter((a) => !validUnlockedAchievements.includes(a.id));
    }

    return filtered;
  }, [selectedCategory, selectedRarity, unlockFilter, validUnlockedAchievements]);

  const unlockedCount = validUnlockedAchievements.length;
  const totalCount = ACHIEVEMENTS_LIST.length;
  const lockedCount = totalCount - unlockedCount;
  const percentage = Math.round((unlockedCount / totalCount) * 100);

  const rarityStats = useMemo(() => {
    const stats = {};
    Object.keys(RARITY_CONFIG).forEach((rarity) => {
      const total = ACHIEVEMENTS_LIST.filter((a) => a.rarity === rarity).length;
      const unlocked = ACHIEVEMENTS_LIST.filter(
        (a) => a.rarity === rarity && validUnlockedAchievements.includes(a.id)
      ).length;
      stats[rarity] = { total, unlocked };
    });
    return stats;
  }, [validUnlockedAchievements]);

  // 計算成就進度
  const getAchievementProgress = (achievement) => {
    if (validUnlockedAchievements.includes(achievement.id)) {
      return { current: 1, target: 1, percentage: 100 };
    }

    const id = achievement.id;
    let current = 0;
    let target = 0;

    // 敲擊成就
    if (id.startsWith('tap_')) {
      target = parseInt(id.split('_')[1]);
      current = Math.min(state.totalTaps, target);
    }
    // 連擊成就
    else if (id.startsWith('combo_')) {
      target = parseInt(id.split('_')[1]);
      current = Math.min(state.maxCombo, target);
    }
    // 速度成就
    else if (id.startsWith('speed_')) {
      const speedMap = { speed_5: 500, speed_3: 200, speed_1: 100 };
      target = speedMap[id] || 0;
      current =
        state.fastestTapInterval < Infinity
          ? Math.max(0, target - state.fastestTapInterval)
          : 0;
    }
    // 類型成就
    else if (
      id.includes('merit') ||
      id.includes('love') ||
      id.includes('wealth') ||
      id.includes('health')
    ) {
      const typeMap = {
        merit_devotee: { type: 'merit', target: 500 },
        merit_legend: { type: 'merit', target: 2000 },
        love_seeker: { type: 'love', target: 500 },
        love_master: { type: 'love', target: 1000 },
        love_legend: { type: 'love', target: 2000 },
        wealth_collector: { type: 'wealth', target: 500 },
        wealth_tycoon: { type: 'wealth', target: 1000 },
        wealth_legend: { type: 'wealth', target: 2000 },
        health_guardian: { type: 'health', target: 500 },
        health_sage: { type: 'health', target: 1000 },
        health_legend: { type: 'health', target: 2000 },
        buddha: { type: 'merit', target: 1000 }
      };
      const config = typeMap[id];
      if (config) {
        target = config.target;
        current = Math.min(state.typeStats[config.type]?.tapCount || 0, target);
      }
    }
    // 自動敲擊成就
    else if (id.startsWith('auto_')) {
      const autoMap = {
        auto_beginner: 1,
        auto_100: 100,
        auto_1000: 1000,
        auto_5000: 5000,
        auto_lover: 10,
        lazy_master: 10000
      };
      target = autoMap[id] || 0;
      current =
        id === 'auto_beginner' || id === 'auto_lover'
          ? Math.min(state.autoTapCount, target)
          : Math.min(state.autoTapTotal, target);
    }
    // 時段成就
    else if (id.includes('night') || id.includes('morning')) {
      const timeMap = {
        night_owl: { stat: 'nightTaps', target: 50 },
        early_bird: { stat: 'morningTaps', target: 50 },
        midnight_devotion: { stat: 'nightTaps', target: 200 },
        morning_blessing: { stat: 'morningTaps', target: 200 }
      };
      const config = timeMap[id];
      if (config) {
        target = config.target;
        current = Math.min(state[config.stat] || 0, target);
      }
    }
    // 會話成就
    else if (id.startsWith('session_') || id === 'marathon') {
      const sessionMap = {
        session_100: 100,
        session_500: 500,
        session_1000: 1000,
        marathon: 2000
      };
      target = sessionMap[id] || 0;
      current = Math.min(state.sessionTaps, target);
    }

    const percentage = target > 0 ? Math.round((current / target) * 100) : 0;
    return { current, target, percentage: Math.min(percentage, 100) };
  };

  return (
    <div className="achievements-modal-content">
      {/* Header */}
      <div className="modal-header-sticky">
        <div>
          <h3 className="text-2xl font-bold text-amber-800 flex items-center gap-2">
            <SafeIcon icon={FiAward} />
            {t('achievements.title')}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{t('achievements.subtitle')}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label={t('common.close')}
        >
          <SafeIcon icon={FiX} className="text-xl" />
        </button>
      </div>

      {/* Progress */}
      <div className="progress-section mb-6">
        <div className="flex justify-between mb-2">
          <span className="font-medium">{t('achievements.progress')}</span>
          <span className="font-bold text-amber-600">
            {unlockedCount}/{totalCount} ({percentage}%)
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
          <motion.div
            className="bg-gradient-to-r from-amber-400 via-orange-500 to-amber-600 h-4 rounded-full relative"
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <div className="absolute inset-0 bg-white/20 animate-pulse" />
          </motion.div>
        </div>
      </div>

      {/* 🆕 解鎖狀態篩選 */}
      <div className="flex gap-2 mb-4">
        <motion.button
          className={`flex-1 py-3 rounded-lg font-bold text-sm transition-all ${
            unlockFilter === 'all'
              ? 'bg-amber-500 text-white shadow-md'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          onClick={() => setUnlockFilter('all')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center justify-center gap-2">
            <span>📋</span>
            <span>
              {t('achievements.allAchievements') || t('common.all')} ({totalCount})
            </span>
          </div>
        </motion.button>

        <motion.button
          className={`flex-1 py-3 rounded-lg font-bold text-sm transition-all ${
            unlockFilter === 'unlocked'
              ? 'bg-green-500 text-white shadow-md'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          onClick={() => setUnlockFilter('unlocked')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center justify-center gap-2">
            <SafeIcon icon={FiCheck} />
            <span>
              {t('achievements.unlockedAchievements') || t('common.unlocked')} ({unlockedCount})
            </span>
          </div>
        </motion.button>

        <motion.button
          className={`flex-1 py-3 rounded-lg font-bold text-sm transition-all ${
            unlockFilter === 'locked'
              ? 'bg-blue-500 text-white shadow-md'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          onClick={() => setUnlockFilter('locked')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center justify-center gap-2">
            <SafeIcon icon={FiLock} />
            <span>
              {t('achievements.lockedAchievements') || t('common.locked')} ({lockedCount})
            </span>
          </div>
        </motion.button>
      </div>

      {/* 稀有度徽章 */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6">
        {Object.entries(RARITY_CONFIG).map(([rarity, config]) => {
          const stats = rarityStats[rarity];
          const isSelected = selectedRarity === rarity;
          return (
            <motion.button
              key={rarity}
              className={`text-center p-3 rounded-lg border-2 transition-all cursor-pointer ${
                isSelected
                  ? 'border-opacity-100 shadow-md'
                  : 'border-gray-200 hover:border-opacity-50'
              }`}
              style={{
                borderColor: isSelected ? config.color : undefined,
                backgroundColor: isSelected ? `${config.color}15` : '#f9fafb'
              }}
              onClick={() => setSelectedRarity(isSelected ? 'all' : rarity)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <div className="text-xs font-bold mb-1" style={{ color: config.color }}>
                {t(`achievements.rarity.${rarity}`) || config.name}
              </div>
              <div className="text-sm font-bold text-gray-700">
                {stats.unlocked}/{stats.total}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Category Filter */}
      <div className="filters mb-6">
        <div className="text-sm font-medium mb-2 text-gray-700">
          {t('achievements.filterByCategory')}
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <motion.button
              key={category.id}
              className={`px-3 py-1.5 rounded-full text-sm transition-all flex items-center gap-1 ${
                selectedCategory === category.id
                  ? 'bg-amber-500 text-white shadow-md'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              onClick={() => setSelectedCategory(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>{category.icon}</span>
              <span>{category.name}</span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Achievements Grid */}
      <div className="achievements-grid">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <AnimatePresence mode="popLayout">
            {filteredAchievements.map((achievement, index) => {
              const isUnlocked = validUnlockedAchievements.includes(achievement.id);
              const rarityConfig = RARITY_CONFIG[achievement.rarity];
              const title =
                t(`achievementsList.${achievement.id}.title`) || achievement.title;
              const description =
                t(`achievementsList.${achievement.id}.description`) ||
                achievement.description;
              const progress = getAchievementProgress(achievement);

              return (
                <motion.div
                  key={achievement.id}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2, delay: index * 0.02 }}
                  className={`achievement-card p-4 rounded-xl border-2 transition-all relative overflow-hidden ${
                    isUnlocked
                      ? 'bg-white border-opacity-100 shadow-md'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                  style={{ borderColor: isUnlocked ? rarityConfig.color : undefined }}
                >
                  {/* Rarity Glow */}
                  {isUnlocked && (
                    <div
                      className="absolute inset-0 opacity-10"
                      style={{
                        background: `radial-gradient(circle at top right, ${rarityConfig.color}, transparent)`
                      }}
                    />
                  )}

                  {/* Lock Overlay */}
                  {!isUnlocked && (
                    <div className="absolute top-2 right-2">
                      <SafeIcon icon={FiLock} className="text-gray-400" />
                    </div>
                  )}

                  <div className="relative">
                    <div className="flex items-start gap-3 mb-2">
                      <div className={`text-3xl ${!isUnlocked && 'opacity-40'}`}>
                        {achievement.icon || '🏆'}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-base mb-1">{title}</h4>
                        <p className="text-sm text-gray-600">{description}</p>
                      </div>
                    </div>

                    {/* 進度條 (未解鎖時顯示) */}
                    {!isUnlocked && progress.target > 0 && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-500">進度</span>
                          <span className="font-bold text-gray-700">
                            {progress.current.toLocaleString()} /{' '}
                            {progress.target.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <motion.div
                            className="h-2 rounded-full"
                            style={{
                              background: `linear-gradient(to right, ${rarityConfig.color}, ${rarityConfig.color}dd)`
                            }}
                            initial={{ width: 0 }}
                            animate={{ width: `${progress.percentage}%` }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                        <div className="text-xs text-gray-500 mt-1 text-right">
                          {progress.percentage}%
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                      <span
                        className="text-xs font-bold px-2 py-1 rounded"
                        style={{
                          color: rarityConfig.color,
                          backgroundColor: `${rarityConfig.color}20`
                        }}
                      >
                        {t(`achievements.rarity.${achievement.rarity}`) || rarityConfig.name}
                      </span>
                      {achievement.unlocksFish && (
                        <div className="text-xs text-amber-600 font-medium flex items-center gap-1">
                          <span>🐠</span>
                          <span>{t('achievements.unlocksFish')}</span>
                        </div>
                      )}
                      {isUnlocked && (
                        <div className="text-green-500 font-bold text-sm">
                          ✓ {t('common.unlocked')}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {filteredAchievements.length === 0 && (
        <div className="text-center text-gray-500 py-12">
          <div className="text-4xl mb-2">🔍</div>
          <p>{t('achievements.noResults')}</p>
        </div>
      )}

      {/* Info Section */}
      <div className="info-section mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
        <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
          <span>💡</span>
          <span>{t('achievements.tips.title')}</span>
        </h4>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• {t('achievements.tips.tip1')}</li>
          <li>• {t('achievements.tips.tip2')}</li>
          <li>• {t('achievements.tips.tip3')}</li>
          <li>• {t('achievements.tips.tip4')}</li>
        </ul>
      </div>
    </div>
  );
};

export default AchievementsModal;